import { Component, Input } from '@angular/core';
import { PMFormatCredit } from '../../../../data/interfaces/circle-credit.interface';

@Component({
  selector: 'app-circle-credit-credits-card',
  templateUrl: './circle-credit-credits-card.component.html',
  styleUrls: ['./circle-credit-credits-card.component.scss']
})
export class CircleCreditCreditsCardComponent {
  @Input() data: PMFormatCredit[] | null = null;
  
  private expandedItems = new Set<number>();

  get hasData(): boolean {
    return !!(this.data && this.data.length > 0);
  }

  trackByIndex(index: number, item: PMFormatCredit): number {
    return index;
  }

  hasAdditionalInfo(credit: PMFormatCredit): boolean {
    return !!(
      credit.maxCreditUsed !== undefined ||
      credit.restructuringDate ||
      credit.observationKey ||
      credit.currency
    );
  }

  toggleCreditDetails(index: number): void {
    if (this.expandedItems.has(index)) {
      this.expandedItems.delete(index);
    } else {
      this.expandedItems.add(index);
    }
  }

  isExpanded(index: number): boolean {
    return this.expandedItems.has(index);
  }

  get completionPercentage(): number {
    if (!this.data || this.data.length === 0) return 0;
    
    const totalFields = this.data.length * 10;
    let filledFields = 0;
    
    this.data.forEach(credit => {
      const criticalFields = [
        'rfc', 'contractNumber', 'creditType', 'initialBalance',
        'openingDate', 'termMonths', 'paymentAmount', 'outstandingBalance',
        'lastPaymentDate', 'creditExperiences'
      ];
      
      filledFields += criticalFields.filter(field => 
        credit[field as keyof PMFormatCredit] !== undefined &&
        credit[field as keyof PMFormatCredit] !== null &&
        credit[field as keyof PMFormatCredit] !== ''
      ).length;
    });
    
    return Math.round((filledFields / totalFields) * 100);
  }

  get statusClass(): string {
    if (!this.hasData) return 'status-empty';
    const percentage = this.completionPercentage;
    if (percentage < 30) return 'status-incomplete';
    if (percentage < 70) return 'status-partial';
    return 'status-complete';
  }

  get totalCredits(): number {
    return this.data?.length || 0;
  }

  get totalOutstandingBalance(): number {
    if (!this.data) return 0;
    return this.data.reduce((sum, credit) => sum + (credit.outstandingBalance || 0), 0);
  }

  get activeCredits(): number {
    if (!this.data) return 0;
    return this.data.filter(credit => 
      credit.outstandingBalance && credit.outstandingBalance > 0
    ).length;
  }

  get creditHealthStatus(): 'excellent' | 'good' | 'fair' | 'poor' {
    if (!this.data || this.data.length === 0) return 'poor';
    
    const activeCount = this.activeCredits;
    const onTimeCredits = this.data.filter(credit => 
      !credit.firstDefaultDate && credit.outstandingBalance && credit.outstandingBalance > 0
    ).length;
    
    const onTimeRatio = activeCount > 0 ? onTimeCredits / activeCount : 1;
    
    if (onTimeRatio >= 0.9) return 'excellent';
    if (onTimeRatio >= 0.7) return 'good';
    if (onTimeRatio >= 0.5) return 'fair';
    return 'poor';
  }

  formatCurrency(amount: number | undefined): string {
    if (!amount && amount !== 0) return 'No especificado';
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount);
  }

  formatDate(date: Date | string | undefined): string {
    if (!date) return 'No especificado';
    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      return dateObj.toLocaleDateString('es-MX');
    } catch {
      return 'Fecha inválida';
    }
  }

  getCreditTypeDisplay(type: string | undefined): string {
    if (!type) return 'No especificado';
    
    const typeMap: { [key: string]: string } = {
      'CC': 'Tarjeta de Crédito',
      'CR': 'Crédito Revolvente',
      'PH': 'Hipotecario',
      'AU': 'Automotriz',
      'PE': 'Personal',
      'CO': 'Comercial'
    };
    
    return typeMap[type] || type;
  }

  getCreditStatus(credit: PMFormatCredit): 'active' | 'paid' | 'defaulted' | 'closed' {
    if (credit.settlementDate) return 'paid';
    if (credit.firstDefaultDate) return 'defaulted';
    if (credit.outstandingBalance && credit.outstandingBalance > 0) return 'active';
    return 'closed';
  }

  getCreditStatusDisplay(credit: PMFormatCredit): string {
    const status = this.getCreditStatus(credit);
    const statusMap = {
      'active': 'Activo',
      'paid': 'Liquidado',
      'defaulted': 'En Mora',
      'closed': 'Cerrado'
    };
    return statusMap[status];
  }

  getCreditStatusClass(credit: PMFormatCredit): string {
    const status = this.getCreditStatus(credit);
    return `status-${status}`;
  }

  getPaymentFrequencyDisplay(frequency: string | undefined): string {
    if (!frequency) return 'No especificado';
    
    const frequencyMap: { [key: string]: string } = {
      'M': 'Mensual',
      'Q': 'Quincenal',
      'S': 'Semanal',
      'B': 'Bimestral',
      'T': 'Trimestral',
      'A': 'Anual'
    };
    
    return frequencyMap[frequency] || frequency;
  }
}
